import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AiChat.css';

const AiChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // Check for dark mode on component mount and when theme changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.body.classList.contains('dark-mode');
      setIsDarkMode(isDark);
    };

    checkDarkMode();
    
    // Listen for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history on component mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('app_token');
      if (!token) return;

      const response = await axios.get('/api/ai-chat/history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Convert chat history to messages format
      const historyMessages = response.data.flatMap(chat => [
        { type: 'user', content: chat.message, timestamp: chat.created_at },
        { 
          type: 'ai', 
          content: chat.response, 
          articles: chat.articles_suggested || [],
          timestamp: chat.created_at 
        }
      ]);

      setChatHistory(response.data);
      setMessages(historyMessages);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message immediately
    const newUserMessage = {
      type: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('app_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post('/api/ai-chat/send', 
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        type: 'ai',
        content: response.data.response,
        articles: response.data.articles || [],
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        type: 'ai',
        content: 'Sorry, I encountered an error while processing your message. Please try again.',
        articles: [],
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArticleClick = (articleId) => {
    // For local/dev: navigate(`/articles/${articleId}`)
    // For EC2/production: change base path if needed
    navigate(`/articles/${articleId}`);
    // Example for EC2: window.location.href = `https://your-ec2-domain.com/articles/${articleId}`;
  };

  const clearChatHistory = async () => {
    try {
      const token = localStorage.getItem('app_token');
      if (!token) return;

      await axios.delete('/api/ai-chat/history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages([]);
      setChatHistory([]);
    } catch (error) {
      console.error('Error clearing chat history:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Utility to render markdown-style article links as clickable spans
  function renderMessageWithLinks(text, onArticleClick) {
    const regex = /\[([^\]]+)\]\(ID: (\d+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // Only render if match[2] exists and is a valid number
      const articleId = match[2];
      if (articleId && /^\d+$/.test(articleId)) {
        parts.push(
          <span
            key={articleId}
            className="ai-article-link"
            onClick={() => onArticleClick(articleId)}
          >
            {match[1]}
          </span>
        );
      } else {
        // Fallback: render the raw match if something is wrong
        parts.push(match[0]);
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  }

  return (
    <div className={`ai-chat-container ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Custom Header with Back Button */}
      <header className="ai-chat-custom-header">
        <div className="ai-chat-header-inner">
          <div className="ai-chat-header-side-left">
            <button 
              onClick={() => navigate(-1)} 
              className="material-icons ai-chat-back-button"
              title="Go back"
            >
              arrow_back
            </button>
          </div>

          <div className="ai-chat-header-center">
            <h1 className="ai-chat-header-title">AI Assistant</h1>
            <p className="ai-chat-header-subtitle">Ask me anything about educational content, career guidance, or personal development!</p>
          </div>

          <div className="ai-chat-header-side-right">
            {chatHistory.length > 0 && (
              <button 
                onClick={clearChatHistory}
                className="clear-chat-btn"
                title="Clear chat history"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="ai-chat-messages">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon">🤖</div>
            <h3>Welcome to AI Assistant!</h3>
            <p>I'm here to help you find relevant articles and answer your questions about:</p>
            <ul>
              <li>Educational content and courses</li>
              <li>Career guidance and development</li>
              <li>Personal growth and skills</li>
              <li>Professional advice and tips</li>
            </ul>
            <p>Just type your question below and I'll help you find the most relevant articles!</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.type === 'user' ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-content">
                <div className="message-text">
                  {message.type === 'ai'
                    ? renderMessageWithLinks(message.content, handleArticleClick)
                    : message.content}
                </div>
                {message.articles && message.articles.length > 0 && (
                  <div className="suggested-articles">
                    <h4>📚 Suggested Articles:</h4>
                    <div className="article-cards">
                      {message.articles.map((article, articleIndex) => (
                        <div 
                          key={articleIndex}
                          className="article-card"
                          onClick={() => handleArticleClick(article.id)}
                        >
                          <div className="article-title">{article.title}</div>
                          <div className="article-relevance">
                            Relevance: {Math.round(article.relevance_score * 100)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="message ai-message">
            <div className="message-content">
              <div className="loading-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={sendMessage} className="ai-chat-input-form">
        <div className="input-container">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Ask me about educational content, career advice, or any topic..."
            disabled={isLoading}
            className="message-input"
          />
          <button 
            type="submit" 
            disabled={isLoading || !inputMessage.trim()}
            className="send-button"
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AiChat; 